//Libs
import { useCallback, useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { DateTime } from "luxon";
import ArrowLeft from "@mui/icons-material/ArrowLeft";
import ArrowRight from "@mui/icons-material/ArrowRight";
import TodayIcon from "@mui/icons-material/Today";
import StarsIcon from "@mui/icons-material/Stars";
import { ZodError } from "zod";
import { Timestamp } from "firebase/firestore";
import cloneDeep from "lodash/cloneDeep";
import DirectionsResult = google.maps.DirectionsResult;
import { Wrapper } from "@googlemaps/react-wrapper";

//Style
import "react-datepicker/dist/react-datepicker.css";
import "../../assets/css/Calendar.css";

//Local
import { DbRead, DbWrite } from "../../database";
import { ExistingTask } from "../../models/task";
import { useUserPermissionsStore } from "../../store/user-permissions";
import { logger } from "../../logging";
import SchedulingSearchBox from "../../components/scheduling/SchedulingSearchBox";
import { convertLuxonDTToFSTimestamp, createToastMessageID } from "../../utils";
import { ColumnOption } from "../../models/scheduling-types";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { useSiteKeyLocationsStore } from "../../store/site-key-locations";
import { ExistingSiteKeyLocation } from "../../models/site-key-location";
import { StyledTooltip } from "../../components/StyledTooltip";
import { useSiteKeyUsersStore } from "../../store/site-key-users";
import ResourcesColumnDropdown from "../../components/ResourcesColumnDropdown";
import {
  AssignedRoute_UpdateAPI,
  ExistingAssignedRoute,
} from "../../models/assigned-route";
import getCurrentJSDateWithoutSeconds from "../../assets/js/getCurrentJSDateWithoutSeconds";
import SchedulingDetailPage from "./SchedulingDetailPage";
import { ExistingVehicle } from "../../models/vehicle";
import { ExistingSiteKeyUserDoc } from "../../models/site-key-users";
import SchedulingDetailResourceView from "../../components/scheduling/SchedulingDetailResourceView";
import {
  getImproperlyGeocodedTasks,
  SchedulingButton,
} from "./SchedulingContainer";
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";
import { determineIfTaskIsGeocoded } from "../../assets/js/scheduling/generateCalendarEvents";
import { allocateTask } from "../../assets/js/scheduling/allocateTask";
import * as strings from "../../strings";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import { useToastMessageStore } from "../../store/toast-messages";
import { generateTotalRouteDistances } from "../../assets/js/scheduling/generateTotalRouteDistances";
import PillMetric from "../../components/PillMetric";
import { TaskDetailsDialogContainer } from "../../components/TaskDetailsDialog";
import { ExistingSiteKeyUserPermissions } from "../../models/site-key-user-permissions";
import { getPermissionsMap } from "../../utils/getPermissionsMap";
import MultipleUidDialog, {
  IMultipleUid_AssignUser,
} from "../../components/CustomFields/MultipleUidDialog";
import currencyFormatter from "../../currency";
import { parseSmartSchedulingFormValues } from "../../models/smart-scheduling-options";
import { VroomRoute } from "../../models/vroom";
import { OptimizedTasks } from "../../assets/js/scheduling/allocateResourcesToTasksVROOM";
import MultipleSelectionDropdown from "../../components/MultipleSelectionDropdown";
import getDirections from "../../assets/js/scheduling/getDirections";
import getMapMarkers from "../../assets/js/scheduling/getMapMarkers";

interface Props {
  siteKey: string;
}

export interface ResourceTechnicianVehicle {
  vehicle: ExistingVehicle | null;
  technicians: ExistingSiteKeyUserDoc[];
  siteKeyLocations: ExistingSiteKeyLocation[];
  permissionsMap: Record<string, ExistingSiteKeyUserPermissions>;
  taskList: ExistingTask[];
  totalOfEstimates: number;
  vroomRoute: VroomRoute | null;
}

interface UnroutableTask {
  taskId: string;
  reason: string;
  originalVehicleId?: string;
  originalTechnicianIds?: string[];
}

// FIXME: console warning -- [Violation] Forced reflow while executing JavaScript took xx ms

// REFACTOR: handle case -- if the only tasks for the selected date are not
// assigned a resource (i.e. they're in the unassigned column on scheduling board)

// FIXME: assignedToDialog/MultipleUidDialog ... bugs.

// This is a page that will have a master-detail layout, where the left panel is
// going to be a list of resources (vehicles, technicians, etc), and the right panel
// will be a list of tasks that are assigned to the selected resource.
// In the right panel, it will also show a map view and a timeline showing the
// breakdown of driving, working, gaps, etc.
// Other information to be displayed would be total dollar amount of the
// estimates and invoices, task and job notes, etc.

// The left panel will have a dropdown to select the type of resource to view.

export default function SchedulingDetailContainer({ siteKey }: Props) {
  const googleMapsApiKey = import.meta.env.VITE_APP_GOOGLE_MAP_KEY;

  const addMessage = useToastMessageStore((state) => state.addToastMessage);
  const { siteKeyUserPermissions: userPermissions } = useUserPermissionsStore();
  const { siteKeyUsersList, loading: isLoadingSiteKeyUsers } =
    useSiteKeyUsersStore();
  const { siteKeyDoc, loading: isLoadingSiteKeyDoc } = useSiteKeyDocStore();
  const { siteKeyLocationList, loading: isLoadingSiteKeyLocations } =
    useSiteKeyLocationsStore();

  /* STATES & VARIABLES */
  const [isUpdatingAssignedUser, setIsUpdatingAssignedUser] = useState(false);
  const [scheduledAwaitingTaskList, setScheduledAwaitingTaskList] = useState<
    ExistingTask[]
  >([]);

  const [vroomRoutesMap, setVroomRoutesMap] = useState<
    Record<string, VroomRoute>
  >({});
  /*
  const [
    isViewingTemporarilyOptimizedTasks,
    setIsViewingTemporarilyOptimizedTasks,
  ] = useState<boolean>(false);
  const [isSavingOptimizedTasks, setIsSavingOptimizedTasks] =
    useState<boolean>(false);
    */
  const [optimizedTasks, setOptimizedTasks] = useState<OptimizedTasks>({
    tasks: [],
    unassigned: [],
    vroomRoutesMap: {},
    vroomOutput: null,
    stats: {
      originalTotalDistanceMiles: 0,
      optimizedTotalDistanceMeters: 0,
      unassignedCount: 0,
      unassignedReasons: [],
    },
  });
  /*
  const [stiltCalendarEvents, setStiltCalendarEvents] = useState<
    ExistingStiltCalendarEvent[]
  >([]);
  */
  const [searchTerm, setSearchTerm] = useState("");
  const [locationsDropdownValue, setLocationsDropdownValue] = useState<
    ColumnOption["value"][]
  >([]);

  const [resourcesDropdownValue, setResourcesDropdownValue] = useState<
    ColumnOption["value"] | null
  >("technicians");
  const [resourcesList, setResourcesList] = useState<
    ResourceTechnicianVehicle[]
  >([]);
  const [selectedDate, setSelectedDate] = useState<Date>(
    getCurrentJSDateWithoutSeconds(),
  );

  const [filteredUsersList, setFilteredUsersList] = useState<
    ExistingSiteKeyUserDoc[]
  >([]);

  const [isOptimizing, setIsOptimizing] = useState(false);
  const [totalDistance, setTotalDistance] = useState("false");
  const [totalDistanceLoading, setTotalDistanceLoading] = useState(false);

  const [taskDetailsDialogOpen, setTaskDetailsDialogOpen] = useState(false);
  const [assignedToDialogOpen, setAssignedToDialogOpen] = useState(false);

  const [vehicleList, setVehicleList] = useState<ExistingVehicle[]>([]);

  const [selectedResource, setSelectedResource] =
    useState<ResourceTechnicianVehicle | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<ExistingTask | null>(
    null,
  );

  const [dailyVehicleUsersMap, setDailyVehicleUsersMap] = useState<
    ExistingAssignedRoute["vehicleUsersMap"]
  >({});

  const [selectedTask, setSelectedTask] = useState<ExistingTask | null>(null);
  const [hoveredTaskID, setHoveredTaskID] = useState<string | null>(null);

  const [stopMarkers, setStopMarkers] = useState<React.ReactNode[]>([]);
  const [startEndMarkers, setStartEndMarkers] = useState<React.ReactNode[]>([]);

  const dispatchBoardResourceTypeDefault: "technicians" | "vehicles" =
    siteKeyDoc?.customizations["dispatchBoardResourceTypeDefault"] ??
    "technicians";

  const [permissionsMap, setPermissionsMap] = useState<
    Record<string, ExistingSiteKeyUserPermissions>
  >({});

  const [estimateTotalsMap, setEstimateTotalsMap] = useState<
    Record<ExistingTask["id"], number>
  >({});

  const [unroutableTasks, setUnroutableTasks] = useState<UnroutableTask[]>([]);

  const [directionsResultAll, setDirectionsResultAll] = useState<
    Record<string, DirectionsResult>
  >({});
  const [directionsResultFiltered, setDirectionsResultFiltered] = useState<
    Record<string, DirectionsResult>
  >({});

  useEffect(() => {
    if (siteKeyUsersList.length === 0) return;
    const list = siteKeyUsersList.filter((user) => user.systemUser !== true);
    setFilteredUsersList(list);
  }, [siteKeyUsersList]);

  /*
  useEffect(() => {
    function getCalendarEventListBySelectedDate() {
      // typeguard
      if (!userPermissions) {
        devLogger.debug("No userPermissions yet...");
        return undefined;
      }
      // Start loading.
      if (isMounted.current) setIsLoading(true);

      //setting the date selected to doesn't care about the hours selected but
      // check the entire day
      const startDate = DateTime.fromJSDate(selectedDate)
        .set({
          hour: 0,
          minute: 0,
          second: 0,
        })
        .minus({ days: 30 });
      const endDate = DateTime.fromJSDate(selectedDate)
        .set({
          hour: 23,
          minute: 59,
          second: 59,
        })
        .plus({ days: 30 });

      // Query for all awaiting scheduled tasks via realtime updates.
      const unsubscribeCalendarEventList =
        DbRead.calendarEvents.subscribeByDate({
          siteKey: siteKey,
          permissions: userPermissions,
          userID: firebaseUser.uid,
          startDate: convertLuxonDTToFSTimestamp(startDate),
          endDate: convertLuxonDTToFSTimestamp(endDate),
          onChange: (events) => {
            setStiltCalendarEvents(events);
          },
          onError: (error) =>
            devLogger.error(
              `Error in ${getCalendarEventListBySelectedDate.name}: ${error.message}`,
            ),
        });

      // If user hasn't navigated away from the page, stop rendering the loading icon.
      if (isMounted.current) {
        setIsLoading(false);
      }
      return unsubscribeCalendarEventList;
    }

    const unsubscribe = getCalendarEventListBySelectedDate();

    // Return an anonymous ƒn for cleanup.
    return () => {
      isMounted.current = false;

      if (unsubscribe) unsubscribe();
    };
  }, [firebaseUser.uid, selectedDate, siteKey, userPermissions]);
  */

  useEffect(() => {
    if (filteredUsersList.length === 0) return;

    async function getUsersPermissionsMap() {
      const result = await getPermissionsMap(filteredUsersList, siteKey);
      setPermissionsMap(result);
    }
    getUsersPermissionsMap();
  }, [siteKey, filteredUsersList]);

  useEffect(() => {
    async function getDistances() {
      if (scheduledAwaitingTaskList.length === 0) return;
      if (siteKeyLocationList.length === 0) return;

      setTotalDistanceLoading(true);
      const totalDistance = await generateTotalRouteDistances({
        tasks: scheduledAwaitingTaskList,
        vehicleList:
          resourcesDropdownValue === "technicians" ? undefined : vehicleList,
        technicianList:
          resourcesDropdownValue === "technicians"
            ? filteredUsersList
            : undefined,
        permissionsMap:
          resourcesDropdownValue === "technicians" ? permissionsMap : undefined,
        siteKeyLocations: siteKeyLocationList,
      });
      setTotalDistance(totalDistance.toFixed(0));
      setTotalDistanceLoading(false);
    }

    getDistances();
  }, [
    filteredUsersList,
    permissionsMap,
    resourcesDropdownValue,
    scheduledAwaitingTaskList,
    siteKeyLocationList,
    vehicleList,
  ]);

  useEffect(() => {
    function subscribeToVehicleList() {
      if (!siteKey) return undefined;
      const unsubscribeVehicles = DbRead.vehicles.subscribeAll({
        siteKey: siteKey,
        onChange: setVehicleList,
        onError: (error) => {
          logger.error(`Error in subscribeToVehicleList: ${error.message}`);
        },
      });
      return unsubscribeVehicles;
    }
    const unsubscribe = subscribeToVehicleList();
    return () => unsubscribe && unsubscribe();
  }, [siteKey]);

  // Populate the resources list based off the users, vehicles, and tasks
  useEffect(() => {
    if (
      scheduledAwaitingTaskList.length === 0 ||
      !hasOptimized.current || // this prevents the effect from running before getTheSteps is finished. getTheSteps calls setScheduledAwaitingTaskList
      (resourcesDropdownValue === "vehicles" && vehicleList.length === 0) ||
      filteredUsersList.length === 0 ||
      Object.keys(permissionsMap).length === 0 ||
      siteKeyLocationList.length === 0
    ) {
      return;
    }

    if (resourcesDropdownValue === "vehicles") {
      // Loop through vehicles and create a resource list
      const resources: ResourceTechnicianVehicle[] = [];
      const clonedVehicles = cloneDeep(vehicleList);
      clonedVehicles
        .sort((a, b) => a.title.localeCompare(b.title))
        .forEach((vehicle) => {
          const vehicleTasks = scheduledAwaitingTaskList.filter(
            (task) => task.taskSpecificDetails.assignedVehicleID === vehicle.id,
          );

          // Filter by location if locations are selected
          if (locationsDropdownValue.length > 0) {
            const hasTasksInSelectedLocations = vehicleTasks.some(
              (task) =>
                task.locationID &&
                locationsDropdownValue.includes(task.locationID),
            );
            if (!hasTasksInSelectedLocations) {
              return;
            }
          }

          const assignedTechnicianIDs =
            dailyVehicleUsersMap[vehicle.id] ?? null;
          let assignedTechnicians: ExistingSiteKeyUserDoc[] = [];
          if (assignedTechnicianIDs) {
            assignedTechnicians = filteredUsersList.filter((user) =>
              assignedTechnicianIDs.includes(user.id),
            );
          }

          // Calculate the total of the estimates based off the tasks
          const vehicleEstimates = vehicleTasks.map(
            (task) => estimateTotalsMap[task.id] ?? 0,
          );
          const totalOfEstimates = vehicleEstimates.reduce(
            (acc, curr) => acc + curr,
            0,
          );

          const resource: ResourceTechnicianVehicle = {
            vehicle: vehicle,
            technicians: assignedTechnicians,
            taskList: vehicleTasks,
            siteKeyLocations: siteKeyLocationList,
            totalOfEstimates,
            permissionsMap: permissionsMap,
            vroomRoute: vroomRoutesMap[vehicle.id],
          };

          // Filter by search term
          if (
            searchTerm &&
            searchTerm !== "" &&
            !checkIfSearchTermExists({
              vehicle,
              users: assignedTechnicians,
              resourceTasks: vehicleTasks,
              searchTerm,
            })
          ) {
            return;
          }

          resources.push(resource);
        });

      // Now we want to filter it so the resources with >0 tasks are first, sorted by vehicle title alphabetically, then the rest
      const resourcesWithTasks = resources.filter(
        (resource) => resource.taskList.length > 0,
      );
      const resourcesWithoutTasks = resources.filter(
        (resource) => resource.taskList.length === 0,
      );
      resourcesWithTasks.sort((a, b) => {
        if (a.vehicle && b.vehicle) {
          return a.vehicle.title.localeCompare(b.vehicle.title);
        } else {
          return 0;
        }
      });
      resourcesWithoutTasks.sort((a, b) => {
        if (a.vehicle && b.vehicle) {
          return a.vehicle.title.localeCompare(b.vehicle.title);
        } else {
          return 0;
        }
      });
      const resourcesSorted = resourcesWithTasks.concat(resourcesWithoutTasks);

      // console.log(
      //   "%csetting resourcesList",
      //   "font-weight:600;color:salmon",
      //   resourcesSorted,
      // );
      setResourcesList(resourcesSorted);
    } else {
      // Loop through technicians and create a resource list
      const resources: ResourceTechnicianVehicle[] = [];
      filteredUsersList.forEach((user) => {
        // if (user.systemUser) return;

        const userTasks = scheduledAwaitingTaskList.filter(
          (task) =>
            task.taskSpecificDetails.assignedTo &&
            Array.isArray(task.taskSpecificDetails.assignedTo) &&
            task.taskSpecificDetails.assignedTo.includes(user.id),
        );

        // Because the vroom optimization makes dummy vehicles, we might get duplicate tasks with the same ID. Let's remove those
        const uniqueTasks = userTasks.filter(
          (task, index, self) =>
            index === self.findIndex((t) => t.id === task.id),
        );

        // Filter by location if locations are selected
        if (locationsDropdownValue.length > 0) {
          const hasTasksInSelectedLocations = uniqueTasks.some(
            (task) =>
              task.locationID &&
              locationsDropdownValue.includes(task.locationID),
          );
          if (!hasTasksInSelectedLocations) {
            return;
          }
        }

        const assignedVehicleID = Object.keys(dailyVehicleUsersMap).find(
          (userIDs) => userIDs.includes(user.id),
        );
        const assignedVehicle = vehicleList.find(
          (vehicle) => vehicle.id === assignedVehicleID,
        );

        // Calculate the total of the estimates based off the tasks
        const userEstimates = userTasks.map((task) => {
          return estimateTotalsMap[task.id] ?? 0;
        });

        const totalOfEstimates = userEstimates.reduce(
          (acc, curr) => acc + curr,
          0,
        );

        const resource: ResourceTechnicianVehicle = {
          vehicle: assignedVehicle ?? null,
          technicians: [user],
          taskList: uniqueTasks,
          siteKeyLocations: siteKeyLocationList,
          totalOfEstimates,
          permissionsMap: permissionsMap,
          vroomRoute: assignedVehicle
            ? vroomRoutesMap[assignedVehicle?.id]
            : (vroomRoutesMap[user.id] ?? null),
        };

        // Filter by search term
        if (
          searchTerm &&
          searchTerm !== "" &&
          !checkIfSearchTermExists({
            vehicle: assignedVehicle ?? null,
            users: [user],
            resourceTasks: userTasks,
            searchTerm,
          })
        ) {
          return;
        }

        resources.push(resource);
      });

      // Now we want to filter it so the resources with >0 tasks are first, sorted alphabetically, then the rest
      const resourcesWithTasks = resources.filter(
        (resource) => resource.taskList.length > 0,
      );
      const resourcesWithoutTasks = resources.filter(
        (resource) => resource.taskList.length === 0,
      );
      resourcesWithTasks.sort((a, b) =>
        a.technicians[0].displayName.localeCompare(
          b.technicians[0].displayName,
        ),
      );
      resourcesWithoutTasks.sort((a, b) =>
        a.technicians[0].displayName.localeCompare(
          b.technicians[0].displayName,
        ),
      );
      const resourcesSorted = resourcesWithTasks.concat(resourcesWithoutTasks);
      // console.log(
      //   "%csetting resourcesList",
      //   "font-weight:600;color:salmon",
      //   resourcesSorted,
      // );
      setResourcesList(resourcesSorted);
    }
  }, [
    searchTerm,
    scheduledAwaitingTaskList,
    dailyVehicleUsersMap,
    vehicleList,
    resourcesDropdownValue,
    filteredUsersList,
    locationsDropdownValue,
    estimateTotalsMap,
    permissionsMap,
    siteKeyLocationList,
    vroomRoutesMap,
  ]);

  useEffect(() => {
    async function getTasksForSelectedDate() {
      // if (!userPermissions) return undefined;
      if (!userPermissions) return;
      const startDate = DateTime.fromJSDate(selectedDate).set({
        hour: 0,
        minute: 0,
        second: 0,
      });
      const endDate = DateTime.fromJSDate(selectedDate).set({
        hour: 23,
        minute: 59,
        second: 59,
      });

      // TODO: enable later... direction lines get redrawn (colors change several
      // times) every time one of the tasks changes... not horrible, but not ideal.
      // also if you're interacting with the map when a task is updated, zoom kinda resets
      // const unsubscribe = DbRead.tasks.subscribeScheduledAwaitingTaskList({
      //   siteKey: siteKey,
      //   permissions: userPermissions,
      //   startDate: convertLuxonDTToFSTimestamp(startDate),
      //   endDate: convertLuxonDTToFSTimestamp(endDate),
      //   onChange: setScheduledAwaitingTaskList,
      //   onError: (error) =>
      //     logger.error(`getTasksForSelectedDate: ${error.message}`, error),
      // });
      // return unsubscribe;
      const todaysTasks = await DbRead.tasks.getScheduledAwaitingTaskList({
        siteKey: siteKey,
        startDate: convertLuxonDTToFSTimestamp(startDate),
        endDate: convertLuxonDTToFSTimestamp(endDate),
      });
      setScheduledAwaitingTaskList(todaysTasks);
    }
    getTasksForSelectedDate();
    // const unsubFn = getTasksForSelectedDate();
    // return () => unsubFn && unsubFn();
  }, [selectedDate, siteKey, userPermissions]);

  useEffect(() => {
    async function getEstimateTotals() {
      if (scheduledAwaitingTaskList.length === 0) return;
      // Loop through the tasks and get the estimate totals
      const estimatePromises = scheduledAwaitingTaskList.map(async (task) => {
        return await DbRead.estimates.getByTaskId(siteKey, task.id);
      });
      const taskEstimates = await Promise.all(estimatePromises);
      // Flatten to a single array
      const estimates = taskEstimates.flat();

      const estimateTotals: Record<ExistingTask["id"], number> = {};
      // Loop through the tasks and update the estimate totals map
      scheduledAwaitingTaskList.forEach((task) => {
        const taskEstimates = estimates.filter(
          (estimate) => estimate.taskID === task.id,
        );
        // Set to the highest estimate customData.totalAmount
        const totalAmounts = taskEstimates.map(
          (estimate) => estimate.customData.totalAmount ?? 0,
        );
        if (totalAmounts.length > 0) {
          const highestTotalAmount = Math.max(...totalAmounts);
          estimateTotals[task.id] = highestTotalAmount;
        } else {
          estimateTotals[task.id] = 0;
        }
      });
      setEstimateTotalsMap(estimateTotals);
    }
    getEstimateTotals();
  }, [scheduledAwaitingTaskList, siteKey]);

  useEffect(() => {
    async function getDirectionResponses() {
      if (
        resourcesList.length === 0 ||
        Object.keys(permissionsMap).length === 0 ||
        siteKeyLocationList.length === 0 ||
        !hasOptimized.current // this prevents the effect from running before getTheSteps is finished. getTheSteps calls setScheduledAwaitingTaskList. changes to scheduledAwaitingTaskList trigger the effect that sets resourcesList. this effect will run when resourcesList changes.
      ) {
        return;
      }

      try {
        const res = await getDirections({
          resources: resourcesList,
          siteKeyLocations: siteKeyLocationList,
          permissionsMap: permissionsMap,
          mapCenter: {
            lat: siteKeyDoc?.latitude ?? 0,
            lng: siteKeyDoc?.longitude ?? 0,
          },
        });
        setDirectionsResultAll(res);
      } catch (e) {
        logger.error(e instanceof Error ? e.message : JSON.stringify(e));
      }
    }
    getDirectionResponses();
  }, [
    permissionsMap,
    resourcesList,
    siteKeyDoc?.latitude,
    siteKeyDoc?.longitude,
    siteKeyLocationList,
  ]);

  useEffect(() => {
    if (selectedResource) {
      const resourceID = selectedResource.vehicle
        ? selectedResource.vehicle.id
        : selectedResource.technicians[0].id;
      const found = directionsResultAll[resourceID];
      if (found) setDirectionsResultFiltered({ [resourceID]: found });
      else setDirectionsResultFiltered({});
    } else {
      setDirectionsResultFiltered(directionsResultAll);
    }
  }, [directionsResultAll, selectedResource]);

  const assignedUserList = useRef<IMultipleUid_AssignUser[]>([]);
  useEffect(() => {
    if (siteKeyUsersList.length === 0) return;

    assignedUserList.current = siteKeyUsersList
      // we don't want system users to be selectable
      .filter((user) => user.systemUser !== true)
      .map((user) => ({
        uid: user.id,
        name: user.displayName,
        company: user.companyName,
        avatarUrl: user.userPhoto_URL,
        isAssigned: false,
        assignedVehicle: null,
      }));
  }, [siteKeyUsersList]);

  useEffect(() => {
    function getDailyVehicleUsersMap() {
      if (!userPermissions) return undefined;

      const unsubscribeDailyAssignment =
        DbRead.assignedRoutes.subscribeDailyVehicleUsersMap({
          siteKey: siteKey,
          isoDate: DateTime.fromJSDate(selectedDate).toISODate(),
          onChange: setDailyVehicleUsersMap,
          onError: (error) => {
            logger.error(
              `Error in ${getDailyVehicleUsersMap.name}: ${error.message}`,
            );
          },
        });

      return unsubscribeDailyAssignment;
    }

    const unsubscribe = getDailyVehicleUsersMap();
    return () => unsubscribe && unsubscribe();
  }, [selectedDate, siteKey, userPermissions]);

  useEffect(() => {
    if (!siteKeyDoc) return;
    if (
      dispatchBoardResourceTypeDefault === "vehicles" &&
      vehicleList.length === 0
    ) {
      return;
    }
    setResourcesDropdownValue(dispatchBoardResourceTypeDefault);
  }, [dispatchBoardResourceTypeDefault, siteKeyDoc, vehicleList.length]);

  useEffect(() => {
    if (resourcesList.length === 0) return;
    if (siteKeyLocationList.length === 0) return;
    if (Object.keys(permissionsMap).length === 0) return;

    const { stopMarkers, startEndMarkers } = getMapMarkers({
      resources: selectedResource ? [selectedResource] : resourcesList,
      siteKeyLocations: siteKeyLocationList,
      permissionsMap,
      hoverTaskID: hoveredTaskID,
      selectedMarker,
      setSelectedMarker,
      mapCenter: {
        lat: siteKeyDoc?.latitude ?? 0,
        lng: siteKeyDoc?.longitude ?? 0,
      },
    });
    setStopMarkers(stopMarkers);
    setStartEndMarkers(startEndMarkers);
  }, [
    hoveredTaskID,
    permissionsMap,
    resourcesList,
    selectedMarker,
    selectedResource,
    siteKeyDoc?.latitude,
    siteKeyDoc?.longitude,
    siteKeyLocationList,
  ]);

  async function handleAssignUsersToVehicle(selectedUserIDs: string[]) {
    if (!selectedResource?.vehicle?.id) return;
    //write on assignedRoute collection the vehicleID with the selectedUserIDs
    dailyVehicleUsersMap[selectedResource.vehicle.id] = selectedUserIDs;

    const dataToSend: AssignedRoute_UpdateAPI = {
      assignedRouteID: DateTime.fromJSDate(selectedDate).toISODate(),
      siteKey: siteKey,
      vehicleUsersMap: dailyVehicleUsersMap,
    };

    //when write to db nees to send a customID (the selectedDate.toIsoDate()) & the dailyAssignment record
    try {
      setIsUpdatingAssignedUser(true);
      await DbWrite.assignedRoutes.update(dataToSend);
      addMessage({
        id: createToastMessageID(),
        message: strings.successfulUpdate(
          `Assigned Route for the vehicle: ${selectedResource.vehicle.title} for the day ${DateTime.fromJSDate(selectedDate).toISODate()}`,
        ),
        dialog: true,
        type: "success",
      });

      resetAssignedUserList(assignedUserList.current);

      setIsUpdatingAssignedUser(false);
    } catch (error) {
      logger.error(
        `handleAssignUsersToVehicle error: ${error instanceof Error ? error.message : JSON.stringify(error)}`,
      );
      addMessage({
        id: createToastMessageID(),
        message: strings.UNEXPECTED_ERROR,
        dialog: true,
        type: "error",
      });
      setIsUpdatingAssignedUser(false);
    }
  }

  function getFormattedSiteLocations(
    siteKeyLocationList: ExistingSiteKeyLocation[],
  ) {
    const columnOptions: ColumnOption[] = siteKeyLocationList.map(
      (siteLocation) => {
        return {
          text: siteLocation.title,
          value: siteLocation.id,
        };
      },
    );
    return columnOptions;
  }

  /*
  function getSiteKeyLocationSelected(
    siteKeyLocationList: ExistingSiteKeyLocation[],
    locationsDropdownValue: ColumnOption["value"][],
  ): ExistingSiteKeyLocation[] {
    if (
      locationsDropdownValue.length === 0 ||
      siteKeyLocationList.length === 1
    ) {
      return siteKeyLocationList;
    } else {
      const result: ExistingSiteKeyLocation[] = [];
      locationsDropdownValue.forEach((selectedLocation) => {
        const found = siteKeyLocationList.find(
          (siteLocation) => siteLocation.id === selectedLocation,
        );

        if (found) {
          result.push(found);
        }
      });
      return result;
    }
  }
  */

  const getLocationDoc = useCallback(
    (locationID: string): ExistingSiteKeyLocation => {
      const found = siteKeyLocationList.find(
        (location) => location.id === locationID,
      );
      if (!found) throw new Error(`Location not found: ${locationID}`);
      return found;
    },
    [siteKeyLocationList],
  );

  /*
  function getTodaysStiltEvents(): ExistingStiltCalendarEvent[] {
    return stiltCalendarEvents.filter((event) => {
      const start = DateTime.fromMillis(event.start.toMillis());
      return (
        start.toISODate() === DateTime.fromJSDate(selectedDate).toISODate()
      );
    });
  }
  */

  const hasOptimized = useRef(false);

  useEffect(() => {
    async function getTheSteps() {
      // Return if we've already optimized or if data isn't ready
      if (
        hasOptimized.current ||
        !siteKeyDoc ||
        !scheduledAwaitingTaskList.length ||
        !siteKeyLocationList.length ||
        totalDistance === "false" || // initial value
        (resourcesDropdownValue === "vehicles" && !vehicleList.length) ||
        (resourcesDropdownValue === "technicians" && !siteKeyUsersList.length)
      ) {
        return;
      }

      setIsOptimizing(true);
      try {
        // Geocode all tasks where geocoding is necessary
        const tasksToGeocode: string[] = [];
        for (const task of scheduledAwaitingTaskList) {
          if (
            !determineIfTaskIsGeocoded(siteKeyDoc, siteKeyLocationList, task)
          ) {
            tasksToGeocode.push(task.id);
          }
        }

        if (tasksToGeocode.length > 0) {
          logger.debug("Geocoding tasks", tasksToGeocode);
          await DbWrite.tasks.geocode(siteKey, tasksToGeocode);
        }

        // Reset unroutable tasks when getting new tasks
        setUnroutableTasks([]);

        let vehiclesToVroom = vehicleList;
        let tasksToVroom = scheduledAwaitingTaskList;
        if (resourcesDropdownValue === "technicians") {
          tasksToVroom = [];
          // Create dummy vehicles for each technician
          vehiclesToVroom = siteKeyUsersList.map((user) => {
            let latitude = siteKeyLocationList[0].latitude;
            let longitude = siteKeyLocationList[0].longitude;
            let locationID = siteKeyLocationList[0].id;
            if (siteKeyLocationList.length > 1) {
              // If there are multiple siteKeyLocations, then we need to find the location for this user's first task of the day
              const userTasks = scheduledAwaitingTaskList.filter((task) => {
                if (
                  task.taskSpecificDetails.assignedTo &&
                  Array.isArray(task.taskSpecificDetails.assignedTo)
                ) {
                  return task.taskSpecificDetails.assignedTo.includes(user.id);
                }
                return false;
              });
              if (userTasks.length > 0) {
                const firstTaskOfTheDay = userTasks.sort((a, b) => {
                  // sort by timestampScheduled
                  if (!a.timestampScheduled || !b.timestampScheduled) return 0;
                  return (
                    a.timestampScheduled.toMillis() -
                    b.timestampScheduled.toMillis()
                  );
                })[0];
                const locationDoc = getLocationDoc(
                  firstTaskOfTheDay.locationID,
                );
                latitude = locationDoc.latitude;
                longitude = locationDoc.longitude;
                locationID = firstTaskOfTheDay.locationID;

                // Loop through the tasks and add them to the tasksToVroom array but swap assignedVehicleID to the user.id
                const userTasksToVroom = userTasks.map((task) => ({
                  ...task,
                  taskSpecificDetails: {
                    ...task.taskSpecificDetails,
                    assignedVehicleID: user.id,
                  },
                }));
                tasksToVroom = [...tasksToVroom, ...userTasksToVroom];
              }
            }
            return {
              id: user.id,
              name: user.displayName,
              vehicleID: user.id,
              refPath: user.refPath,
              title: user.displayName,
              description: user.displayName,
              craftTypes: [],
              locationID: locationID,
              latitude: latitude,
              longitude: longitude,
              customID: null,
              qrCode: null,
              thumbnailURL: null,
              defaultSiteKeyUserID: [],
              timestampCreated: Timestamp.now(),
              timestampLastModified: Timestamp.now(),
              createdBy: user.id,
              lastModifiedBy: user.id,
              deleted: false,
              taskTypes: [],
              maxTasksPerDay: 8,
            };
          });
        }

        // Store original assignments before optimization
        const originalAssignments = tasksToVroom.map((task) => ({
          taskId: task.id,
          vehicleId: task.taskSpecificDetails.assignedVehicleID,
          technicianIds: task.taskSpecificDetails.assignedTo,
        }));

        const result = await allocateTask({
          siteKeyID: siteKey,
          originalTotalDistanceMiles: Number(totalDistance),
          vehicleList: vehiclesToVroom,
          scheduledTaskList: tasksToVroom,
          currentDateString: DateTime.fromJSDate(selectedDate).toISODate(),
          siteKeyWorkTypes: siteKeyDoc.validCraftTypes,
          siteKeyTaskTypes: siteKeyDoc.validTaskTypes,
          getLocationDoc: getLocationDoc,
          smartSchedulingSettings: parseSmartSchedulingFormValues(
            siteKeyDoc?.customizations.smartSchedulingSettings,
          ),
          lockAllVehicles: true,
        });

        // Process unassigned tasks and restore their original assignments
        const unroutableTasksList: UnroutableTask[] = [];
        const restoredTasks = [...result.tasks];

        result.unassigned.forEach((unassignedTask) => {
          originalAssignments.forEach((originalAssignment) => {
            if (originalAssignment.taskId === unassignedTask.description) {
              // Then check if the task was routable, since the task might have been routable for one user but not for another user
              // Just a reminder, we use the userIDs as vehicleIDs when in technician mode
              const originalAssignmentVehicleID = originalAssignment.vehicleId;
              if (typeof originalAssignmentVehicleID === "string") {
                const originalAssignmentRoute =
                  result.vroomRoutesMap[originalAssignmentVehicleID];
                if (
                  originalAssignmentRoute?.steps.some(
                    (step) => step.description === unassignedTask.description,
                  )
                ) {
                  // This task was routable for the original assignment so we don't want it to show up as an unroutable task
                  return;
                } else {
                  // This task was not routable for the original assignment so we want it to show up as an unroutable task

                  // Push to unRoutableTasksList if there's not already an unroutable task with the same taskId and vehicleID
                  if (
                    !unroutableTasksList.some(
                      (ut) =>
                        ut.taskId === unassignedTask.description &&
                        ut.originalVehicleId === originalAssignmentVehicleID,
                    )
                  ) {
                    // Find the task in the result list and restore its original assignments
                    const taskIndex = restoredTasks.findIndex(
                      (task) => task.id === unassignedTask.description,
                    );

                    if (taskIndex !== -1) {
                      restoredTasks[taskIndex] = {
                        ...restoredTasks[taskIndex],
                        taskSpecificDetails: {
                          ...restoredTasks[taskIndex].taskSpecificDetails,
                          assignedVehicleID: originalAssignment.vehicleId,
                          assignedTo: originalAssignment.technicianIds,
                        },
                      };
                    }

                    unroutableTasksList.push({
                      taskId: unassignedTask.description,
                      reason: unassignedTask.reason ?? "Unknown reason",
                      originalVehicleId: originalAssignmentVehicleID,
                      originalTechnicianIds:
                        originalAssignment.technicianIds as string[],
                    });
                  }
                }
              }
            }
          });
        });

        setIsOptimizing(false);
        setScheduledAwaitingTaskList(restoredTasks);
        setOptimizedTasks(result);
        setVroomRoutesMap(result.vroomRoutesMap);
        setUnroutableTasks(unroutableTasksList);
        hasOptimized.current = true;
      } catch (e) {
        if (e instanceof ZodError) {
          logger.info("🚨 ZOD ERROR:", e.flatten().fieldErrors);
        }
        logger.error("Error optimizing", e);
        setIsOptimizing(false);
        addMessage({
          id: createToastMessageID(),
          message: strings.ERR_ROUTING,
          dialog: false,
          type: "error",
        });
      }
    }
    getTheSteps();
  }, [
    siteKeyDoc,
    scheduledAwaitingTaskList,
    siteKeyLocationList,
    vehicleList,
    resourcesDropdownValue,
    siteKey,
    totalDistance,
    selectedDate,
    getLocationDoc,
    siteKeyUsersList,
    addMessage,
  ]);

  async function optimize() {
    if (!siteKeyDoc) return;
    setIsOptimizing(true);
    try {
      // Geocode all tasks where geocoding is necessary
      const tasksToGeocode: string[] = [];
      scheduledAwaitingTaskList.forEach((task) => {
        if (!determineIfTaskIsGeocoded(siteKeyDoc, siteKeyLocationList, task)) {
          tasksToGeocode.push(task.id);
        }
      });

      if (tasksToGeocode.length > 0) {
        logger.debug("Geocoding tasks", tasksToGeocode);
        await DbWrite.tasks.geocode(siteKey, tasksToGeocode);
      }

      const result = await allocateTask({
        siteKeyID: siteKey,
        originalTotalDistanceMiles: Number(totalDistance),
        vehicleList: vehicleList,
        scheduledTaskList: scheduledAwaitingTaskList,
        currentDateString: DateTime.fromJSDate(selectedDate).toISODate(),
        siteKeyWorkTypes: siteKeyDoc.validCraftTypes,
        siteKeyTaskTypes: siteKeyDoc.validTaskTypes,
        getLocationDoc: getLocationDoc,
        // stiltEvents: getTodaysStiltEvents(),
        smartSchedulingSettings: parseSmartSchedulingFormValues(
          siteKeyDoc?.customizations.smartSchedulingSettings,
        ),
        lockAllVehicles: false,
      });
      logger.info("optimization result", result);
      setIsOptimizing(false);
      setScheduledAwaitingTaskList(result.tasks);
      setVroomRoutesMap(result.vroomRoutesMap);
    } catch (e) {
      if (e instanceof ZodError) {
        logger.info("🚨 ZOD ERROR:", e.flatten().fieldErrors);
      }
      logger.error("Error optimizing", e);
      setIsOptimizing(false);
      addMessage({
        id: createToastMessageID(),
        message: strings.ERR_OPTIMIZING,
        dialog: false,
        type: "error",
      });
    }
  }

  const resourcesColumnSelectorOptions: ColumnOption[] = [
    { value: "technicians", text: "Technicians" },
  ];

  const siteUsesVehicles =
    siteKeyDoc?.customizations.vehiclesEnabled === true &&
    vehicleList.length > 0;

  if (siteUsesVehicles) {
    resourcesColumnSelectorOptions.push({
      value: "vehicles",
      text: "Vehicles",
    });
  }

  const locationsColumnSelectorOptions: ColumnOption[] =
    getFormattedSiteLocations(siteKeyLocationList);

  /*
  // FIXME: implement
  const filteredSiteKeyLocations = getSiteKeyLocationSelected(
    siteKeyLocationList,
    locationsDropdownValue,
  );
  */

  function onChangeDatePicker(date: Date | null): void {
    if (date) {
      setSelectedDate(date);
      // if (!sameDay(selectedDate, date)) setSelectedResource(null);
    }
  }

  function sameDay(d1: Date, d2: Date) {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }

  const totalDistancePill = (
    <StyledTooltip
      title={`${getImproperlyGeocodedTasks(scheduledAwaitingTaskList, siteKeyLocationList).toFixed(0)} Improperly Geocoded Tasks`}
    >
      <PillMetric
        key="totalDistancePill"
        text="Miles"
        value={totalDistance === "false" ? "0" : totalDistance}
        color="gray"
        isLoading={totalDistanceLoading}
      />
    </StyledTooltip>
  );

  const optimizeBtn = resourcesDropdownValue === "vehicles" &&
    siteKeyDoc?.customizations?.allowManualFleetOptimization && (
      <StyledTooltip title={strings.OPTIMIZE_TASKS}>
        <span>
          <BaseButtonPrimary
            onClick={optimize}
            isBusy={isOptimizing}
            busyText={strings.buttons.BUSY_OPTIMIZING}
          >
            <StarsIcon />
          </BaseButtonPrimary>
        </span>
      </StyledTooltip>
    );

  // const optimizeResultsSection = optimizedTasks.stats.unassignedCount > 0 && (
  //   <div className="flex w-full flex-col space-y-2 bg-yellow-50 px-4 py-2">

  //     {optimizedTasks.stats?.originalTotalDistanceMiles && optimizedTasks.stats?.originalTotalDistanceMiles > 0 && (<>
  //       <div className="flex flex-row">
  //         {(() => {
  //           const originalTotalDistanceMiles =
  //             optimizedTasks.stats?.originalTotalDistanceMiles ?? 0;
  //           const optimizedDistanceMeters =
  //             optimizedTasks.stats?.optimizedTotalDistanceMeters ?? 0;
  //           const optimizedDistanceMiles = optimizedDistanceMeters / 1609.34;
  //           const distanceSaved =
  //             Number(originalTotalDistanceMiles) - optimizedDistanceMiles;
  //           const percentSaved =
  //             (distanceSaved / Number(originalTotalDistanceMiles)) * 100;

  //           return (
  //             <div className="flex w-full items-center justify-between">
  //               <div className="font-bold text-green-600">
  //                 Total Miles Driven reduced by {distanceSaved.toFixed(1)} mi (
  //                 {percentSaved.toFixed(1)}%)
  //               </div>
  //             </div>
  //           );
  //         })()}
  //       </div>
  //       <hr className="border-gray-200" />
  //     </>)}

  //     <div className="flex items-center justify-between">
  //       <div className="flex items-center gap-2">
  //         <InformationCircleIcon className="h-9 w-9 text-blue-500" />
  //         <span className="text-blue-500">{strings.OPTIMIZED_TASKS_NOTE}</span>
  //       </div>
  //       <div className="flex gap-2 pl-8 pr-2">
  //         <BaseButtonSecondary
  //           className="uppercase"
  //           onClick={() => setIsViewingTemporarilyOptimizedTasks(false)}
  //         >
  //           {strings.buttons.CANCEL}
  //         </BaseButtonSecondary>
  //         {/* <BaseButtonPrimary
  //             className="uppercase"
  //             onClick={handleUpdateTasksWithOptimizations}
  //             isBusy={isSavingOptimizedTasks}
  //             busyText={strings.buttons.BUSY_SAVING}
  //           >
  //             {strings.SAVE}
  //           </BaseButtonPrimary> */}
  //       </div>
  //     </div>

  //     <div className="text-sm">
  //       {scheduledAwaitingTaskList.filter(
  //         (task) => !task.taskSpecificDetails?.assignedVehicleID,
  //       ).length > 0 && (
  //           <div className="mt-1">
  //             <div className="font-semibold text-amber-800">
  //               <ExclamationTriangleIcon className="mr-1 inline-block h-5 w-5 text-amber-500" />
  //               {
  //                 scheduledAwaitingTaskList.filter(
  //                   (task) => !task.taskSpecificDetails?.assignedVehicleID,
  //                 ).length
  //               }{" "}
  //               unassigned task(s):
  //             </div>
  //             {scheduledAwaitingTaskList
  //               .filter((task) => !task.taskSpecificDetails?.assignedVehicleID)
  //               .map((task, index) => {
  //                 const unassignedReason = optimizedTasks.unassigned.find(
  //                   (u) => u.description === task.id,
  //                 )?.reason;
  //                 return (
  //                   <div key={index} className="text-red-600">
  //                     {task.title} -{" "}
  //                     {unassignedReason || "Could not be assigned to any vehicle"}
  //                   </div>
  //                 );
  //               })}
  //           </div>
  //         )}
  //     </div>
  //   </div>
  // );

  const filtersAndButtons = (
    <div className="flex items-center gap-1">
      <div className="flex flex-row items-center">
        <StyledTooltip title="Previous Day">
          <button
            type="button"
            onClick={() => {
              const previousDay = DateTime.fromJSDate(selectedDate)
                .minus({ day: 1 })
                .set({ hour: 6 });
              setSelectedDate(previousDay.toJSDate());
              // setSelectedResource(null);
            }}
            className="mr-2 h-10 rounded-md border border-gray-300 bg-white px-4 text-gray-800 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primaryLight focus:ring-offset-2"
          >
            <ArrowLeft />
          </button>
        </StyledTooltip>
        {!sameDay(selectedDate, DateTime.now().toJSDate()) && (
          <StyledTooltip title="Today">
            <button
              type="button"
              onClick={() => {
                setSelectedDate(getCurrentJSDateWithoutSeconds());
                // setSelectedResource(null);
              }}
              className="ml-1 mr-2 inline-flex h-10 items-center justify-center rounded-md border border-gray-300 bg-white p-2 text-gray-600 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primaryLight focus:ring-offset-2"
            >
              <TodayIcon />
            </button>
          </StyledTooltip>
        )}
        <div className="w-60">
          <DatePicker
            selected={selectedDate}
            onChange={onChangeDatePicker}
            showTimeSelect={false}
            dateFormat="cccc - M/d/yyyy"
            customInput={<SchedulingButton />}
          />
        </div>
        <StyledTooltip title="Next Day">
          <button
            type="button"
            onClick={() => {
              const nextDay = DateTime.fromJSDate(selectedDate)
                .plus({ day: 1 })
                .set({ hour: 6 });
              setSelectedDate(nextDay.toJSDate());
              // setSelectedResource(null);
            }}
            className="mx-2 h-10 rounded-md border border-gray-300 bg-white px-4 text-gray-800 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primaryLight focus:ring-offset-2"
          >
            <ArrowRight />
          </button>
        </StyledTooltip>
      </div>
      <ResourcesColumnDropdown
        optionList={resourcesColumnSelectorOptions}
        onSelection={(selectedValue) => {
          setResourcesDropdownValue(selectedValue);
        }}
        initialValue={resourcesDropdownValue}
      />
      <MultipleSelectionDropdown
        selectionType="locations"
        onSelection={(multipleSelectionValue: ColumnOption["value"][]) =>
          setLocationsDropdownValue(multipleSelectionValue)
        }
        optionList={locationsColumnSelectorOptions}
        initialValue={locationsDropdownValue}
      />
      {optimizeBtn}
      {totalDistancePill}
    </div>
  );

  /*
  const openJobButton = selectedTask && (
    <BaseButtonPrimary
      className="flex gap-2 uppercase"
      onClick={() => {
        const lux = DateTime.fromJSDate(selectedDate);
        localStorage.setItem(
          "schedulingContainerCurrentDate",
          JSON.stringify(lux.toMillis()),
        );
        goToWorkRecordAndTasksPage(selectedTask.craftRecordID);
      }}
    >
      {strings.buttons.OPEN_JOB}
    </BaseButtonPrimary>
  );
  */

  const assignedToDialog = assignedUserList.current.length > 0 && (
    <MultipleUidDialog
      open={assignedToDialogOpen}
      closeDialog={() => {
        setAssignedToDialogOpen(false);
        resetAssignedUserList(assignedUserList.current);
      }}
      userList={assignedUserList.current}
      handleSubmit={(event) => {
        const selectedList = event.filter((user) => user.isAssigned);
        const selectedUserIDs = selectedList.map((user) => user.uid);
        handleAssignUsersToVehicle(selectedUserIDs);
      }}
    />
  );

  const assignUserButton = selectedResource &&
    resourcesDropdownValue === "vehicles" && (
      <BaseButtonPrimary
        className="mx-2 font-medium uppercase tracking-wide"
        isBusy={isUpdatingAssignedUser}
        onClick={() => {
          const selectedVehicle = selectedResource?.vehicle;

          if (!selectedVehicle) return;

          setAssignedToDialogOpen(true);

          const assignedUsersForSelectedVehicle =
            dailyVehicleUsersMap[selectedVehicle.id];

          for (const [vehicleID, assignedUsers] of Object.entries(
            dailyVehicleUsersMap,
          )) {
            if (selectedVehicle.id === vehicleID) continue;

            const vehicleDoc = vehicleList.filter(
              (vehicle) => vehicle.id === vehicleID,
            );
            assignedUserList.current.forEach((user) => {
              if (assignedUsers.includes(user.uid)) {
                user.assignedVehicle = vehicleDoc[0].title;
              }
            });
          }

          if (assignedUsersForSelectedVehicle?.length > 0) {
            assignedUserList.current.forEach((user) => {
              if (assignedUsersForSelectedVehicle.includes(user.uid)) {
                user.isAssigned = true;
              }
            });
          }
        }}
      >
        {strings.ASSIGN_USERS}
      </BaseButtonPrimary>
    );

  const resourcesListView = (
    <>
      <SchedulingSearchBox
        placeholder="Search Tasks and Resources..."
        handleSearchTerm={setSearchTerm}
        removeMargin={true}
      />
      {selectedResource ? (
        <button
          type="button"
          onClick={() => setSelectedResource(null)}
          className="mt-1 text-sm text-primary underline"
        >
          View All Resources
        </button>
      ) : (
        <p className="mt-1 text-sm text-primary">Viewing All Resources</p>
      )}
      <div className="mt-1 max-h-[calc(100vh-16rem)] w-64 overflow-y-auto">
        {resourcesList.map((resource, i) => {
          return (
            <div
              key={
                resource.vehicle?.id ??
                resource.technicians[0].id ??
                resource.taskList[0].id ??
                i
              }
              className={`cursor-pointer border border-gray-200 p-2 hover:bg-gray-100 ${resource === selectedResource ? "bg-gray-100" : ""}`}
              onClick={() => setSelectedResource(resource)}
            >
              <div className="flex flex-row items-center justify-between">
                {resource.vehicle ? (
                  <div className="font-medium">{resource.vehicle.title}</div>
                ) : (
                  // intentionally empty span element - forces # of tasks to
                  // display on the right if we're in technicians mode
                  <span></span>
                )}

                {resource.taskList.length > 0 && (
                  <span className="rounded-full border border-gray-400 bg-gray-300 px-1.5 py-0 text-sm font-medium text-gray-800">
                    {resource.taskList.length}
                  </span>
                )}
              </div>
              {resource.technicians.map((user) => {
                return <div key={user.id}>{user.displayName}</div>;
              })}
              <div className="text-sm italic text-green-800">{`Estimated Total: ${currencyFormatter(resource.totalOfEstimates, siteKeyDoc?.customizations?.accounting?.currency ?? "USD")}`}</div>
            </div>
          );
        })}
      </div>
    </>
  );

  // Detail view showing a list of all tasks for the selected resource
  const detailView = (
    <SchedulingDetailResourceView
      key={
        selectedResource?.vehicle?.id ??
        selectedResource?.technicians[0]?.id ??
        "no-resource-map"
      }
      allResources={
        locationsDropdownValue.length > 0 && !selectedResource
          ? resourcesList.map((resource) => ({
              ...resource,
              taskList: resource.taskList.filter(
                (task) =>
                  task.locationID &&
                  locationsDropdownValue.includes(task.locationID),
              ),
            }))
          : resourcesList
      }
      selectedResource={selectedResource}
      setSelectedTask={(task) => {
        setSelectedTask(task);
        // setTaskDetailsDialogOpen(Boolean(task));
        setTaskDetailsDialogOpen(true);
      }}
      assignUserButton={assignUserButton}
      unroutableTasks={unroutableTasks}
      directionsResponses={directionsResultFiltered}
      setHoveredTask={setHoveredTaskID}
      children={{
        resourceStartEndMarkers: startEndMarkers,
        resourceMarkers: stopMarkers,
      }}
    />
  );

  const taskDetailsContainer = selectedTask && (
    <TaskDetailsDialogContainer
      assets={[]}
      onAssetClick={() => {}}
      selectedTaskID={selectedTask.id}
      vehicleList={vehicleList}
      setTaskDetailsDialogOpen={(open) => {
        if (!open) setSelectedTask(null);
        setTaskDetailsDialogOpen(open);
      }}
      permissionsMap={permissionsMap}
      taskDetailsDialogOpen={taskDetailsDialogOpen}
    />
  );

  if (
    isLoadingSiteKeyDoc ||
    isLoadingSiteKeyUsers ||
    isLoadingSiteKeyLocations
  ) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <LoadingClipboardAnimation />
      </div>
    );
  }

  const noTasksForSelectedDate = scheduledAwaitingTaskList.length === 0 && (
    <p className="ml-12 mt-4 text-base text-gray-500">
      No tasks found for the selected date
    </p>
  );

  return (
    <Wrapper apiKey={googleMapsApiKey ?? ""} libraries={["places"]}>
      <SchedulingDetailPage
        siteKey={siteKey}
        optimizeResultsSection={null}
        filtersAndButtons={filtersAndButtons}
        resourcesListView={resourcesListView}
        detailView={detailView}
      >
        {{ noTasksForSelectedDate: noTasksForSelectedDate }}
      </SchedulingDetailPage>
      {assignedToDialog}
      {taskDetailsContainer}
    </Wrapper>
  );
}

/** Searches technician names, vehicle titles, and task titles */
function checkIfSearchTermExists({
  vehicle,
  users,
  resourceTasks,
  searchTerm,
}: {
  vehicle: ExistingVehicle | null;
  users: ExistingSiteKeyUserDoc[];
  /** Tasks that "belong" to the current vehicle or technician */
  resourceTasks: ExistingTask[];
  searchTerm: string;
}): boolean {
  if (vehicle?.title.toLowerCase().includes(searchTerm.toLowerCase())) {
    return true;
  }

  const userNames = users.map((user) => user.displayName);
  const userNamesIncludesSearchTerm = userNames.some((name) =>
    name.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  if (userNamesIncludesSearchTerm) {
    return true;
  }

  const taskTitles = resourceTasks.map((task) => task.title);
  const yesTaskTitle = taskTitles.some((title) =>
    title.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  if (yesTaskTitle) return true;

  return false;
}

function resetAssignedUserList(
  assignedUserList: IMultipleUid_AssignUser[],
): void {
  assignedUserList.forEach((user) => {
    user.isAssigned = false;
    user.assignedVehicle = null;
  });

  // const newList: IMultipleUid_AssignUser[] = [];
  // for (const user of assignedUserList) {
  //   newList.push({ ...user, isAssigned: false });
  // }
  // return newList;
}
